import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'carbon-react/lib/components/button';
import { RenderOpenProps } from 'carbon-react/lib/components/popover-container';

import { AccountCategory } from '@futrli-sdk/predict';
import { PopoverMenu } from '@futrli/ui-components/components/popover-menu/popover-menu';
import { usePopoverRowShield } from '@futrli/ui-components/hooks/usePopoverRowShield';

import { COA_TYPE, COADisplayAccount, SWITCH_RULE_CATEGORIES } from '../../types';
import { DragHandlers } from '../../hooks/useCategoryDragDrop';
import { useCoaActions } from '../context/coa-actions-context';

import * as styles from './group-row-actions.scss';

interface Props {
  isUpdatingStructure: boolean;
  coaRow: COADisplayAccount;
  coaType: COA_TYPE;
  drag: DragHandlers;
  index: number;
}

export const AccountRowActions = ({ isUpdatingStructure, coaRow, coaType, drag, index }: Props) => {
  const attachShieldRef = usePopoverRowShield();
  const { handleReclassifyClick, handleSwitchRuleClick, handleSwitchRuleDeleteClick } = useCoaActions();

  const switchRuleExists =
    SWITCH_RULE_CATEGORIES.indexOf(coaRow.category as AccountCategory) !== -1 && !!coaRow.switch_rule_category;

  const items = [
    {
      label: <FormattedMessage id="common.reclassify" defaultMessage="Reclassify" />,
      hasBorder: coaType === COA_TYPE.PROFIT_AND_LOSS,
      onClick: () => handleReclassifyClick(coaRow)
    },
    ...(coaType === COA_TYPE.BALANCE_SHEET
      ? [
          switchRuleExists
            ? {
                label: <FormattedMessage id="common.remove_switch_rule" defaultMessage="Remove switch rule" />,
                hasBorder: true,
                onClick: () => handleSwitchRuleDeleteClick(coaRow)
              }
            : {
                label: <FormattedMessage id="common.add_switch_rule" defaultMessage="Add switch rule" />,
                hasBorder: true,
                onClick: () => handleSwitchRuleClick(coaRow)
              }
        ]
      : []),
    ...(drag.canMoveUp(index)
      ? [
          {
            label: <FormattedMessage id="common.move_up" defaultMessage="Move up" />,
            onClick: () => drag.onMoveUp(index)
          }
        ]
      : []),
    ...(drag.canMoveDown(index)
      ? [
          {
            label: <FormattedMessage id="common.move_down" defaultMessage="Move down" />,
            onClick: () => drag.onMoveDown(index)
          }
        ]
      : []),
    ...(drag.canMoveToTop(index)
      ? [
          {
            label: <FormattedMessage id="common.move_to_top" defaultMessage="Move to top" />,
            onClick: () => drag.onMoveToTop(index)
          }
        ]
      : []),
    ...(drag.canMoveToBottom(index)
      ? [
          {
            label: <FormattedMessage id="common.move_to_bottom" defaultMessage="Move to bottom" />,
            onClick: () => drag.onMoveToBottom(index)
          }
        ]
      : [])
  ];

  return (
    <div
      // Keep the React synthetic shields to protect against normal clicks
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', color: '#000' }}
      className={`${styles.darkFont} ${isUpdatingStructure ? styles.disabled : ''}`}
    >
      <PopoverMenu
        items={items}
        renderOpenComponent={({ onClick, ref, 'aria-expanded': ariaExpanded }: RenderOpenProps) => (
          <Button
            buttonType="tertiary"
            iconType="ellipsis_vertical"
            iconPosition="after"
            onClick={onClick}
            ref={attachShieldRef(ref)}
            aria-expanded={ariaExpanded}
            size="small"
          >
            <FormattedMessage id="predict.actions" defaultMessage="Actions" />
          </Button>
        )}
      />
    </div>
  );
};
